<template>
  <AccountLayout active-menu="addresses">
    <div class="addresses-page">
      <h1>Address</h1>

      <!-- 地址列表 -->
      <div v-if="addresses.length" class="address-list">
        <div v-for="(address, index) in addresses" :key="address.id" class="address-card"
          :class="{ 'is-default': index === defaultIndex }">
          <div class="address-grid">
            <span>Name:</span>
            <strong>{{ address.name }}</strong>
            <span>Phone:</span>
            <strong>{{ address.phone }}</strong>
            <span>Address:</span>
            <strong class="full-address">{{ address.address }}</strong>
          </div>
          <div class="address-actions">
            <button v-if="index === defaultIndex" class="primary-btn" type="button" disabled>
              Default Address
            </button>
            <button v-else class="text-btn set-default" type="button" @click="setDefault(index)">
              Set as default address
            </button>
            <div>
              <button class="text-btn" type="button" @click="openEdit(index)">Edit</button>
              <button class="text-btn" type="button" @click="removeAddress(index)">Delete</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 无地址时显示协议确认框 -->
      <div v-else class="agreement-box">
        <h2>Agreement required</h2>
        <label>
          <input v-model="agreed" type="checkbox" class="custom-checkbox">
          <span class="check-box"></span>
          <span>
            I allow to store and use my address information by buybuygenuine Trading co., Ltd.<br>
            Given information will be only used to process your orders and may be forwarded to a shipping carrier to
            perform the delivery.
          </span>
        </label>
      </div>

      <!-- 添加 / 编辑表单 -->
      <h2 class="form-title">{{ editingIndex === null ? 'Shipping addresses' : 'Edit address' }}</h2>
      <form class="address-form" @submit.prevent="submitAddress">
        <label><b>*</b>Name</label>
        <el-input v-model="form.name" placeholder="Please enter" class="form-input" :class="{ 'is-error': errors.name }"
          @blur="validateField('name')" />
        <p v-if="errors.name" class="error-msg">{{ errors.name }}</p>

        <label><b>*</b>Country / Region</label>
        <el-input v-model="form.country" placeholder="Please enter" class="form-input"
          :class="{ 'is-error': errors.country }" @blur="validateField('country')" />
        <p v-if="errors.country" class="error-msg">{{ errors.country }}</p>

        <label class="with-hint">
          <span><b>*</b>Address</span>
          <em>example, 26 Norman St, Peakhurst NSW 2210, Australia</em>
        </label>
        <el-input v-model="form.address" placeholder="Street,City,State,Zip Code" class="form-input"
          :class="{ 'is-error': errors.address }" @blur="validateField('address')" />
        <p v-if="errors.address" class="error-msg">{{ errors.address }}</p>

        <label><b>*</b>Phone</label>
        <el-input v-model="form.phone" placeholder="Country code +number" class="form-input"
          :class="{ 'is-error': errors.phone }" @blur="validateField('phone')" />
        <p v-if="errors.phone" class="error-msg">{{ errors.phone }}</p>

        <div class="form-actions">
          <button class="primary-btn add-btn" type="submit">
            {{ editingIndex === null ? 'Add address' : 'Save changes' }}
          </button>
          <button v-if="editingIndex !== null" class="text-btn cancel-btn" type="button" @click="cancelEdit">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </AccountLayout>
</template>

<script>
import AccountLayout from '@/components/common/AccountLayout.vue'
import { getToken } from '@/util/token-util.js'

function emptyForm() {
  return { name: '', country: '', address: '', phone: '' }
}

export default {
  name: 'AddressesPage',
  components: {
    AccountLayout
  },
  data() {
    return {
      agreed: false,
      editingIndex: null,
      defaultIndex: 0,
      form: emptyForm(),
      errors: {},
      addresses: [],
      loading: false
    }
  },
  computed: {
    hasAddress() {
      return this.addresses.length > 0
    }
  },
  mounted() {
    if (!getToken()) {
      this.$message.error('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    this.loadAddresses()
  },
  methods: {
    async loadAddresses() {
      this.loading = true
      try {
        const res = await this.$api({
          url: 'Address/list',
          method: 'get'
        })
        const list = res.data || []
        this.addresses = list.map((item) => ({
          id: item.id,
          name: item.name,
          phone: item.phone,
          country: item.country,
          address: item.detail_address || '',
          is_default: item.is_default,
          raw: item
        }))
        const defIdx = this.addresses.findIndex((a) => a.is_default)
        this.defaultIndex = defIdx >= 0 ? defIdx : 0
      } catch (e) {
        this.$message.error('Failed to load addresses')
      } finally {
        this.loading = false
      }
    },
    validateField(field) {
      const value = (this.form[field] || '').trim()
      if (!value) {
        this.$set(this.errors, field, 'This field is required')
        return false
      }
      if (field === 'phone' && !/^[+\d][\d\s-]{5,}$/.test(value)) {
        this.$set(this.errors, field, 'Please enter a valid phone number')
        return false
      }
      this.$set(this.errors, field, '')
      return true
    },
    validateAll() {
      const fields = ['name', 'country', 'address', 'phone']
      let valid = true
      fields.forEach((f) => {
        if (!this.validateField(f)) valid = false
      })
      return valid
    },
    async submitAddress() {
      if (!this.validateAll()) {
        this.$message.error('Please complete all required fields')
        return
      }
      // 无地址时需先勾选协议
      if (this.addresses.length === 0 && !this.agreed) {
        this.$message.warning('Please accept the agreement first')
        return
      }
      this.loading = true
      try {
        if (this.editingIndex === null) {
          // 新增
          await this.$api({
            url: 'Address/add',
            method: 'post',
            data: {
              name: this.form.name,
              phone: this.form.phone,
              country: this.form.country,
              detail_address: this.form.address
            }
          })
          this.$message.success('Address added')
        } else {
          // 编辑
          await this.$api({
            url: 'Address/update',
            method: 'post',
            data: {
              id: this.addresses[this.editingIndex].id,
              name: this.form.name,
              phone: this.form.phone,
              country: this.form.country,
              detail_address: this.form.address
            }
          })
          this.$message.success('Address updated')
        }
        this.resetForm()
        await this.loadAddresses()
      } catch (e) {
        this.$message.error('Failed to save address')
      } finally {
        this.loading = false
      }
    },
    openEdit(index) {
      const addr = this.addresses[index]
      this.form = { name: addr.name, country: addr.country, address: addr.address, phone: addr.phone }
      this.editingIndex = index
      this.errors = {}
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    },
    cancelEdit() {
      this.resetForm()
    },
    removeAddress(index) {
      this.$confirm('Are you sure to delete this address?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(async () => {
          this.loading = true
          try {
            await this.$api({
              url: 'Address/remove',
              method: 'post',
              data: { id: this.addresses[index].id }
            })
            this.$message.success('Address deleted')
            if (this.editingIndex === index) {
              this.resetForm()
            }
            await this.loadAddresses()
          } catch (e) {
            this.$message.error('Failed to delete address')
          } finally {
            this.loading = false
          }
        })
        .catch(() => { })
    },
    async setDefault(index) {
      this.loading = true
      try {
        await this.$api({
          url: 'Address/setDefault',
          method: 'post',
          data: { id: this.addresses[index].id }
        })
        this.defaultIndex = index
        this.$message.success('Default address updated')
      } catch (e) {
        this.$message.error('Failed to set default address')
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form = emptyForm()
      this.editingIndex = null
      this.errors = {}
    }
  }
}
</script>

<style scoped>
.addresses-page h1,
.form-title {
  margin: 0 0 28px;
  color: #111;
  font-size: 28px;
  font-weight: 400;
}

.address-list {
  margin-bottom: 48px;
}

.address-card {
  margin-bottom: 24px;
  padding: 24px 28px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.address-card.is-default {
  border-color: #F34B01;
}

.address-grid {
  display: grid;
  grid-template-columns: 90px 1fr 90px 1fr;
  gap: 22px 12px;
  color: #333;
  font-size: 16px;
}

.address-grid span {
  color: #555;
}

.full-address {
  grid-column: 2 / 5;
}

.address-actions {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.primary-btn {
  height: 40px;
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  padding: 0 24px;
  font-size: 16px;
}

.primary-btn:disabled {
  cursor: default;
  opacity: 0.85;
}

.text-btn {
  border: 0;
  background: transparent;
  color: #F34B01;
  cursor: pointer;
  font-size: 16px;
  margin-left: 34px;
}

.set-default {
  margin-left: auto;
}

.agreement-box {
  margin-bottom: 28px;
  padding: 20px 28px;
  border-radius: 6px;
  background: #fff1ed;
}

.agreement-box h2 {
  margin: 0 0 14px;
  color: #111;
  font-size: 20px;
  font-weight: 400;
}

.agreement-box label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;
}

.agreement-box .custom-checkbox {
  display: none;
}

.agreement-box .check-box {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  background: url(../../assets/check0.png) no-repeat center/contain;
}

.agreement-box .custom-checkbox:checked+.check-box {
  background-image: url(../../assets/check1.png);
}

.form-title {
  margin-top: 32px;
}

.address-form {
  width: 676px;
}

.address-form label {
  display: block;
  margin: 26px 0 10px;
  color: #111;
  font-size: 14px;
}

.address-form b {
  color: #F34B01;
}

.address-form .form-input {
  width: 100%;
}

.address-form .form-input ::v-deep .el-input__inner {
  width: 100%;
  height: 44px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0 14px;
  color: #333;
  font-size: 14px;
  outline: none;
}

.address-form .form-input ::v-deep .el-input__inner::placeholder {
  color: #aaa;
}

.address-form .form-input.is-error ::v-deep .el-input__inner,
.address-form .form-input.is-error ::v-deep .el-input__inner:focus {
  border-color: #f56c6c;
}

.error-msg {
  margin: 6px 0 0;
  color: #f56c6c;
  font-size: 12px;
}

.with-hint {
  display: flex !important;
  justify-content: space-between;
  align-items: center;
}

.with-hint em {
  color: #999;
  font-style: normal;
  font-size: 13px;
}

.form-actions {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.add-btn {
  width: 140px;
  height: 40px;
  font-size: 14px;
}

.cancel-btn {
  margin-left: 0;
}
</style>
